import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FaChartBar, FaRedo } from 'react-icons/fa';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

const PERIOD_OPTIONS = [
  { value: 30, label: '30 días' },
  { value: 90, label: '90 días' },
  { value: 180, label: '180 días' },
];

const TERM_FIELD_LABELS = {
  nombre_producto: 'Nombre',
  vehiculo: 'Vehículo',
  sku: 'SKU',
  codigo_barras: 'Código barras',
  medidas: 'Medidas',
  nombre_marca: 'Marca producto',
};

const OUTLIER_TABS = [
  { id: 'rareMulti', label: 'Consultas raras' },
  { id: 'unusualMix', label: 'Mezclas inusuales' },
];

const fmtNum = (n) => Number(n || 0).toLocaleString('es-CO');
const fmtPct = (n) => `${((n || 0) * 100).toFixed(1)}%`;

const todayIso = () => new Date().toISOString().slice(0, 10);

const daysAgoIso = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

const KpiCard = ({ label, value, sub }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
    <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    {sub && <p className="mt-0.5 text-xs text-gray-500">{sub}</p>}
  </div>
);

const HorizontalBar = ({ label, pct, count }) => (
  <div className="space-y-1">
    <div className="flex justify-between text-xs">
      <span className="font-medium text-gray-700">{label}</span>
      <span className="text-gray-500">{fmtNum(count)} · {fmtPct(pct)}</span>
    </div>
    <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
      <div
        className="h-full rounded-full bg-yellow-500 transition-all"
        style={{ width: `${Math.min(100, (pct || 0) * 100)}%` }}
      />
    </div>
  </div>
);

const FilterChips = ({ filters }) => (
  <div className="flex flex-wrap gap-1">
    {(filters || []).map((f) => (
      <span
        key={`${f.key}-${f.value}`}
        className="inline-block rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700"
      >
        {f.label}: {f.value}
      </span>
    ))}
  </div>
);

const BuscadorAnalyticsModal = ({
  isOpen,
  onClose,
  token,
  onApplyTerm,
  onApplyQuery,
  onApplyAttribute,
}) => {
  const [scope, setScope] = useState('global');
  const [days, setDays] = useState(90);
  const [dateMode, setDateMode] = useState('preset');
  const [fromDate, setFromDate] = useState(daysAgoIso(90));
  const [toDate, setToDate] = useState(todayIso());
  const [termField, setTermField] = useState('nombre_producto');
  const [outlierTab, setOutlierTab] = useState('rareMulti');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const authHeaders = useMemo(
    () => (token ? { Authorization: `Bearer ${token}` } : {}),
    [token]
  );

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { scope };
      if (dateMode === 'range') {
        params.from = fromDate;
        params.to = toDate;
      } else {
        params.days = days;
      }
      const response = await axios.get('/api/productos/busquedas-analytics', {
        headers: authHeaders,
        params,
      });
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron cargar las estadísticas');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [authHeaders, days, scope, dateMode, fromDate, toDate]);

  useEffect(() => {
    if (!isOpen) return undefined;
    fetchAnalytics();
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, fetchAnalytics]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const handleApplyTerm = (field, value) => {
    if (onApplyTerm) onApplyTerm(field, value);
    onClose();
  };

  const handleApplyQuery = (filters) => {
    if (onApplyQuery) onApplyQuery(filters);
    onClose();
  };

  const handleApplyAttribute = (categoryId, attrId, value) => {
    if (onApplyAttribute) onApplyAttribute(categoryId, attrId, value);
    onClose();
  };

  if (!isOpen) return null;

  const terms = data?.termsByField?.[termField] || [];
  const peakHourLabel = data?.kpis?.peakHour != null
    ? `${String(data.kpis.peakHour).padStart(2, '0')}:00`
    : '—';

  const outlierItems = {
    rareMulti: data?.outliers?.rareMultiFieldQueries || [],
    unusualMix: data?.outliers?.unusualFieldMixes || [],
  }[outlierTab] || [];

  const periodLabel = data?.period?.mode === 'range'
    ? `${data.period.from} → ${data.period.to}`
    : `Últimos ${data?.period?.days || days} días`;

  const avgFields = data?.kpis?.avgFieldsPerSearch != null
    ? data.kpis.avgFieldsPerSearch.toFixed(1)
    : '—';

  return createPortal(
    <div
      className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-gray-50 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-indigo-100 p-2.5 text-indigo-700">
              <FaChartBar className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Estadísticas del buscador</h2>
              <p className="text-xs text-gray-500">
                Consultas frecuentes, patrones y señales atípicas
                {data?.generatedAt && (
                  <span> · {periodLabel}</span>
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-2 py-1 text-2xl leading-none text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            aria-label="Cerrar"
          >
            &times;
          </button>
        </div>

        <div className="flex shrink-0 flex-col gap-2 border-b border-gray-200 bg-white px-5 py-3 sm:px-6">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex rounded-lg border border-gray-200 p-0.5">
              {['global', 'mine'].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScope(s)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    scope === s ? 'bg-indigo-600 text-white' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {s === 'global' ? 'Global' : 'Mis búsquedas'}
                </button>
              ))}
            </div>
            <div className="flex rounded-lg border border-gray-200 p-0.5">
              <button
                type="button"
                onClick={() => setDateMode('preset')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  dateMode === 'preset' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Presets
              </button>
              <button
                type="button"
                onClick={() => setDateMode('range')}
                className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                  dateMode === 'range' ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                Rango
              </button>
            </div>
            <button
              type="button"
              onClick={fetchAnalytics}
              disabled={loading}
              className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 disabled:opacity-50"
            >
              <FaRedo className={loading ? 'animate-spin' : ''} />
              Actualizar
            </button>
          </div>
          {dateMode === 'preset' ? (
            <div className="flex rounded-lg border border-gray-200 p-0.5 w-fit">
              {PERIOD_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setDays(opt.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    days === opt.value ? 'bg-yellow-500 text-black' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <label className="text-xs text-gray-600">
                Desde
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className="mt-0.5 block rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                />
              </label>
              <label className="text-xs text-gray-600">
                Hasta
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className="mt-0.5 block rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                />
              </label>
              <button
                type="button"
                onClick={fetchAnalytics}
                className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                Aplicar rango
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 sm:px-6 space-y-6">
          {loading && !data && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-xl bg-gray-200" />
              ))}
            </div>
          )}

          {error && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
              {error}
              <button type="button" onClick={fetchAnalytics} className="ml-2 font-semibold underline">
                Reintentar
              </button>
            </div>
          )}

          {data?.empty && !loading && (
            <p className="text-center text-gray-500 py-12">No hay búsquedas registradas en este período.</p>
          )}

          {data && !data.empty && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <KpiCard label="Total búsquedas" value={fmtNum(data.kpis.totalSearches)} />
                <KpiCard
                  label="Tasa de éxito"
                  value={fmtPct(data.kpis.successRate)}
                  sub={`${fmtNum(data.kpis.searchesWithResults)} con resultados`}
                />
                <KpiCard
                  label="Prom. campos/búsqueda"
                  value={avgFields}
                  sub="Filtros activos por consulta"
                />
                <KpiCard
                  label={scope === 'global' ? 'Empleados activos' : 'Hora pico'}
                  value={scope === 'global' ? fmtNum(data.kpis.activeEmployees) : peakHourLabel}
                  sub={scope === 'global' ? `pico ${peakHourLabel}` : `${fmtNum(data.kpis.peakHourCount)} búsquedas`}
                />
              </div>

              <section className="rounded-xl border border-indigo-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-1">Consultas más frecuentes</h3>
                <p className="text-xs text-gray-500 mb-3">Combinaciones reales de filtros con valores (no solo nombres de campo).</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                        <th className="pb-2 pr-2 w-8">#</th>
                        <th className="pb-2 pr-2">Consulta</th>
                        <th className="pb-2 pr-2 text-right">Usos</th>
                        <th className="pb-2 text-right">Éxito</th>
                        <th className="pb-2 w-16" />
                      </tr>
                    </thead>
                    <tbody>
                      {(data.frequentQueries || []).slice(0, 15).map((q, i) => (
                        <tr key={q.fingerprint} className="border-b border-gray-50 hover:bg-gray-50/80 align-top">
                          <td className="py-2 pr-2 text-gray-400">{i + 1}</td>
                          <td className="py-2 pr-2">
                            <p className="font-medium text-gray-800 text-xs mb-1">{q.display}</p>
                            <FilterChips filters={q.filters} />
                          </td>
                          <td className="py-2 pr-2 text-right text-gray-600 whitespace-nowrap">{fmtNum(q.count)}</td>
                          <td className="py-2 text-right text-gray-600 whitespace-nowrap">{fmtPct(q.successRate)}</td>
                          <td className="py-2 text-right">
                            {onApplyQuery && (
                              <button
                                type="button"
                                onClick={() => handleApplyQuery(q.filters)}
                                className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                              >
                                Usar
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="rounded-xl border border-teal-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-1">Atributos más consultados</h3>
                <p className="text-xs text-gray-500 mb-3">Por categoría de producto y valor buscado.</p>
                {(data.topAttributesByCategory || []).length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Sin búsquedas con atributos técnicos en este período.</p>
                ) : (
                  <div className="space-y-4">
                    {data.topAttributesByCategory.map((cat) => (
                      <div key={cat.categoryId ?? 'sin-categoria'} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <h4 className="text-xs font-bold text-gray-800">{cat.categoryName}</h4>
                          <span className="text-[10px] text-gray-500">{fmtNum(cat.totalUses)} usos</span>
                        </div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="border-b border-gray-200 text-left text-gray-500">
                                <th className="pb-1.5 pr-2">Atributo</th>
                                <th className="pb-1.5 pr-2">Valor</th>
                                <th className="pb-1.5 pr-2 text-right">Usos</th>
                                <th className="pb-1.5 w-12" />
                              </tr>
                            </thead>
                            <tbody>
                              {cat.attributes.flatMap((attr) =>
                                attr.topValues.map((tv, vi) => (
                                  <tr key={`${attr.attrId}-${tv.value}`} className="border-b border-gray-100/80">
                                    {vi === 0 && (
                                      <td
                                        className="py-1.5 pr-2 font-medium text-gray-700 align-top"
                                        rowSpan={attr.topValues.length}
                                      >
                                        {attr.attrName}
                                        <span className="block text-[10px] font-normal text-gray-400">
                                          {fmtNum(attr.count)} total
                                        </span>
                                      </td>
                                    )}
                                    <td className="py-1.5 pr-2 text-gray-800">{tv.value}</td>
                                    <td className="py-1.5 pr-2 text-right text-gray-600">{fmtNum(tv.count)}</td>
                                    <td className="py-1.5 text-right">
                                      {onApplyAttribute && cat.categoryId && (
                                        <button
                                          type="button"
                                          onClick={() => handleApplyAttribute(cat.categoryId, attr.attrId, tv.value)}
                                          className="text-[10px] font-semibold text-teal-700 hover:text-teal-900"
                                        >
                                          Usar
                                        </button>
                                      )}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Uso de campos</h3>
                  <div className="space-y-3">
                    {data.allFields.slice(0, 8).map((f) => (
                      <HorizontalBar key={f.key} label={f.label} pct={f.pct} count={f.count} />
                    ))}
                  </div>
                </section>

                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Volumen diario</h3>
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={data.dailyTrend} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip formatter={(v) => [fmtNum(v), 'Búsquedas']} />
                        <Area type="monotone" dataKey="count" stroke="#6366f1" fill="#c7d2fe" strokeWidth={2} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              </div>

              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                  <h3 className="text-sm font-bold text-gray-800">Términos por campo</h3>
                  <div className="flex flex-wrap gap-1">
                    {Object.keys(TERM_FIELD_LABELS).map((key) => (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setTermField(key)}
                        className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors ${
                          termField === key
                            ? 'bg-yellow-500 text-black'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        {TERM_FIELD_LABELS[key]}
                      </button>
                    ))}
                  </div>
                </div>
                {terms.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Sin datos para este campo.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                          <th className="pb-2 pr-2 w-8">#</th>
                          <th className="pb-2 pr-2">Término</th>
                          <th className="pb-2 pr-2 text-right">Usos</th>
                          <th className="pb-2 pr-2 text-right">Éxito</th>
                          <th className="pb-2 w-16" />
                        </tr>
                      </thead>
                      <tbody>
                        {terms.slice(0, 12).map((row, i) => (
                          <tr key={`${row.value}-${i}`} className="border-b border-gray-50 hover:bg-gray-50/80">
                            <td className="py-2 pr-2 text-gray-400">{i + 1}</td>
                            <td className="py-2 pr-2 font-medium text-gray-800 max-w-[240px] truncate" title={row.value}>
                              {row.value}
                            </td>
                            <td className="py-2 pr-2 text-right text-gray-600">{fmtNum(row.count)}</td>
                            <td className="py-2 text-right text-gray-600">{fmtPct(row.successRate)}</td>
                            <td className="py-2 text-right">
                              {onApplyTerm && (
                                <button
                                  type="button"
                                  onClick={() => handleApplyTerm(termField, row.value)}
                                  className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800"
                                >
                                  Usar
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>

              <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-3">Patrones de campos</h3>
                <p className="text-xs text-gray-500 mb-2">Qué filtros se combinan (sin valores).</p>
                <div className="space-y-2">
                  {data.topCombinations.slice(0, 10).map((c, i) => (
                    <div key={c.combo} className="flex items-center gap-2 text-xs">
                      <span className="w-5 shrink-0 text-gray-400">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-800 truncate" title={c.labels}>{c.labels}</p>
                        <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className="h-full bg-indigo-400 rounded-full"
                            style={{ width: `${Math.min(100, c.pct * 100)}%` }}
                          />
                        </div>
                      </div>
                      <span className="shrink-0 text-gray-500">{fmtNum(c.count)}</span>
                    </div>
                  ))}
                </div>
              </section>

              <section className="rounded-xl border border-violet-200 bg-violet-50/30 p-4 shadow-sm">
                <h3 className="text-sm font-bold text-gray-800 mb-2">Señales atípicas</h3>
                <div className="flex flex-wrap gap-1 mb-3">
                  {OUTLIER_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => setOutlierTab(tab.id)}
                      className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                        outlierTab === tab.id
                          ? 'bg-violet-600 text-white'
                          : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
                {outlierItems.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Sin señales en esta categoría.</p>
                ) : (
                  <ul className="space-y-3">
                    {outlierItems.map((item) => (
                      <li key={item.fingerprint || item.fieldCombo} className="rounded-lg bg-white border border-gray-100 p-3 text-xs">
                        {outlierTab === 'unusualMix' ? (
                          <p className="font-medium text-gray-800">{item.labels}</p>
                        ) : (
                          <>
                            <p className="font-medium text-gray-800">{item.display}</p>
                            <FilterChips filters={item.filters} />
                          </>
                        )}
                        <p className="mt-1 text-gray-500">{item.reason}</p>
                        <p className="mt-0.5 text-gray-700">
                          {fmtNum(item.count)} usos
                          {item.successRate != null && ` · éxito ${fmtPct(item.successRate)}`}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </section>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Campos por búsqueda</h3>
                  <div className="space-y-2">
                    {data.fieldsPerSearch.map((row) => (
                      <div key={row.fields} className="flex items-center justify-between text-sm">
                        <span className="text-gray-700">
                          {row.fields === 0 ? 'Sin campos' : `${row.fields} campo${row.fields !== 1 ? 's' : ''}`}
                        </span>
                        <span className="font-semibold text-gray-900">
                          {fmtNum(row.count)} <span className="font-normal text-gray-500">({fmtPct(row.pct)})</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </section>

                <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                  <h3 className="text-sm font-bold text-gray-800 mb-3">Distribución por hora</h3>
                  <div className="h-40">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={data.hourlyDistribution} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                        <XAxis dataKey="hour" tick={{ fontSize: 9 }} tickFormatter={(h) => `${h}h`} interval={3} />
                        <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                        <Tooltip labelFormatter={(h) => `${h}:00`} formatter={(v) => [fmtNum(v), 'Búsquedas']} />
                        <Bar dataKey="count" fill="#fbbf24" radius={[2, 2, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </section>
              </div>

              {data.dataQuality?.note && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {data.dataQuality.note}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default BuscadorAnalyticsModal;
